// ManageRewards.js
import React, { useState, useEffect } from 'react';
import { 
  getRewards, 
  createReward, 
  updateReward, 
  deleteReward 
} from '../../api/rewards';
import {
  getRewardsComingSoonOverlayEnabled,
  setRewardsComingSoonOverlayEnabled,
} from '../../api/settings';
import { uploadImage } from '../../utils/imageUpload';
import Button from '../../components/ui/Button';
import './ManageRewards.css';

const ManageRewards = () => {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    merchantName: '',
    title: '',
    description: '',
    discount: '',
    requiredTier: 1,
    category: '',
    terms: '',
    validUntil: '',
    active: true,
  });
  const [imageFile, setImageFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [overlayEnabled, setOverlayEnabled] = useState(false);
  const [overlayLoading, setOverlayLoading] = useState(true);
  const [overlaySaving, setOverlaySaving] = useState(false);

  useEffect(() => {
    fetchRewards();
  }, []);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        setOverlayLoading(true);
        const enabled = await getRewardsComingSoonOverlayEnabled();
        if (isMounted) setOverlayEnabled(enabled);
      } finally {
        if (isMounted) setOverlayLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleToggleOverlay = async () => {
    const next = !overlayEnabled;
    setOverlayEnabled(next);
    setOverlaySaving(true);
    setError(null);
    setSuccess(null);

    try {
      await setRewardsComingSoonOverlayEnabled(next);
      setSuccess(`Rewards overlay ${next ? 'enabled' : 'disabled'} successfully!`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating rewards overlay setting:', err);
      setOverlayEnabled(!next);
      setError('Failed to update rewards overlay setting. Please try again.');
    } finally {
      setOverlaySaving(false);
    }
  };

  const fetchRewards = async () => {
    try {
      setLoading(true);
      const rewardsData = await getRewards();
      setRewards(rewardsData);
      setError(null);
    } catch (err) {
      console.error('Error fetching rewards:', err);
      setError('Failed to load rewards. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value,
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setError('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }

      setImageFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      if (!formData.merchantName || !formData.title || !formData.discount || !formData.requiredTier) {
        setError('Please fill in all required fields');
        setSubmitting(false);
        return;
      }

      let imageUrl = editingReward?.imageUrl || '';
      
      if (imageFile) {
        imageUrl = await uploadImage(imageFile, 'rewards');
      }

      const rewardData = {
        ...formData,
        imageUrl,
      };

      if (editingReward) {
        await updateReward(editingReward.id, rewardData);
        setSuccess('Reward updated successfully!');
      } else {
        await createReward(rewardData);
        setSuccess('Reward created successfully!');
      }

      resetForm();
      await fetchRewards();
      setShowForm(false);
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error submitting reward:', err);
      setError(err.message || 'Failed to save reward. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (reward) => {
    setEditingReward(reward);
    setFormData({
      merchantName: reward.merchantName || '',
      title: reward.title || '',
      description: reward.description || '',
      discount: reward.discount || '',
      requiredTier: reward.requiredTier || 1,
      category: reward.category || '',
      terms: reward.terms || '',
      validUntil: reward.validUntil || '',
      active: reward.active !== undefined ? reward.active : true,
    });
    setImagePreview(reward.imageUrl || null);
    setImageFile(null);
    setShowForm(true);
    setError(null);
  };

  const handleDelete = async (rewardId) => {
    if (!window.confirm('Are you sure you want to delete this reward? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteReward(rewardId);
      setSuccess('Reward deleted successfully!');
      await fetchRewards();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting reward:', err);
      setError('Failed to delete reward. Please try again.');
    }
  };

  const toggleActive = async (reward) => {
    try {
      await updateReward(reward.id, { active: !reward.active });
      setSuccess(`Reward ${!reward.active ? 'activated' : 'deactivated'} successfully!`);
      await fetchRewards();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error toggling reward status:', err);
      setError('Failed to update reward status. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      merchantName: '',
      title: '',
      description: '',
      discount: '',
      requiredTier: 1,
      category: '',
      terms: '',
      validUntil: '',
      active: true,
    });
    setImageFile(null);
    setImagePreview(null);
    setEditingReward(null);
    setError(null);
  };

  const handleCancel = () => {
    resetForm();
    setShowForm(false);
  };

  if (loading) {
    return (
      <div className="manage-rewards">
        <div className="loading">Loading rewards...</div>
      </div>
    );
  }

  return (
    <div className="manage-rewards">
      <div className="manage-rewards-header">
        <h2>Manage Rewards</h2>
        <Button 
          variant="primary" 
          onClick={() => {
            resetForm();
            setShowForm(!showForm);
          }}
        >
          {showForm ? 'Cancel' : '+ Add New Reward'}
        </Button>
      </div>

      <div className="reward-settings-container">
        <h3>Rewards Page Overlay</h3>
        <div className="reward-settings-row">
          <div className="reward-settings-status">
            <span className="reward-settings-label">Coming soon overlay</span>
            <span className={`reward-settings-pill ${overlayEnabled ? 'on' : 'off'}`}>
              {overlayLoading ? 'Loading…' : overlayEnabled ? 'ON' : 'OFF'}
            </span>
          </div>

          <Button
            variant="primary"
            onClick={handleToggleOverlay}
            disabled={overlayLoading || overlaySaving}
          >
            {overlaySaving ? 'Saving…' : overlayEnabled ? 'Turn Off' : 'Turn On'}
          </Button>
        </div>
        <p className="reward-settings-help">
          When enabled, members will see the bilingual announcement overlay on the Rewards page.
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {showForm && (
        <div className="reward-form-container">
          <h3>{editingReward ? 'Edit Reward' : 'Create New Reward'}</h3>
          <form onSubmit={handleSubmit} className="reward-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="merchantName">Merchant Name *</label>
                <input
                  type="text"
                  id="merchantName"
                  name="merchantName"
                  value={formData.merchantName}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Starbucks, McDonald's"
                />
              </div>

              <div className="form-group">
                <label htmlFor="category">Category</label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                >
                  <option value="">Select Category</option>
                  <option value="Food & Dining">Food & Dining</option>
                  <option value="Shopping">Shopping</option>
                  <option value="Entertainment">Entertainment</option>
                  <option value="Health & Wellness">Health & Wellness</option>
                  <option value="Travel">Travel</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="title">Offer Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                placeholder="e.g., Buy 1 Get 1 Free on Coffee"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                placeholder="Describe the offer..."
              ></textarea>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="discount">Discount *</label>
                <input
                  type="text"
                  id="discount"
                  name="discount"
                  value={formData.discount}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., 20%, 10 KD"
                />
              </div>

              <div className="form-group">
                <label htmlFor="requiredTier">Required Tier *</label>
                <select
                  id="requiredTier"
                  name="requiredTier"
                  value={formData.requiredTier}
                  onChange={handleInputChange}
                  required
                >
                  <option value={1}>Tier 1</option>
                  <option value={2}>Tier 2</option>
                  <option value={3}>Tier 3</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="terms">Terms & Conditions</label>
              <textarea
                id="terms"
                name="terms"
                value={formData.terms}
                onChange={handleInputChange}
                rows="3"
                placeholder="List any terms, conditions, or restrictions..."
              ></textarea>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="validUntil">Valid Until</label>
                <input
                  type="date"
                  id="validUntil"
                  name="validUntil"
                  value={formData.validUntil}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                  />
                  <span>Active (visible to members)</span>
                </label>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="image">Merchant Logo/Image</label>
              <input
                type="file"
                id="image"
                accept="image/*"
                onChange={handleImageChange}
              />
              {imagePreview && (
                <div className="image-preview">
                  <img src={imagePreview} alt="Preview" />
                </div>
              )}
            </div>

            <div className="form-actions">
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={submitting}>
                {submitting ? 'Saving...' : editingReward ? 'Update Reward' : 'Create Reward'}
              </Button>
            </div>
          </form>
        </div>
      )}

      <div className="rewards-table-container">
        <table className="rewards-table">
          <thead>
            <tr>
              <th>Merchant</th>
              <th>Offer</th>
              <th>Discount</th>
              <th>Tier</th>
              <th>Category</th>
              <th>Redeemed</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rewards.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-data">
                  No rewards found. Create your first reward to get started!
                </td>
              </tr>
            ) : (
              rewards.map((reward) => (
                <tr key={reward.id}>
                  <td>
                    <div className="merchant-cell">
                      {reward.imageUrl ? (
                        <img src={reward.imageUrl} alt={reward.merchantName} className="merchant-logo" />
                      ) : (
                        <div className="merchant-placeholder">
                          {reward.merchantName?.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span>{reward.merchantName}</span>
                    </div>
                  </td>
                  <td className="offer-title">{reward.title}</td>
                  <td><span className="discount-badge">{reward.discount}</span></td>
                  <td><span className="points-badge">Tier {reward.requiredTier}</span></td>
                  <td>{reward.category || 'N/A'}</td>
                  <td>{reward.redeemedCount || 0}</td>
                  <td>
                    <button
                      className={`status-badge ${reward.active ? 'active' : 'inactive'}`}
                      onClick={() => toggleActive(reward)}
                      title="Click to toggle status"
                    >
                      {reward.active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-edit"
                        onClick={() => handleEdit(reward)}
                        title="Edit reward"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(reward.id)}
                        title="Delete reward"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ManageRewards;
